<template>
    <div>
        <w-app
            :title="'Paie'"
            show-total
            :total="total"
        >

            <template v-slot:leftToolbar>
                <ImportModel file-name="Paie.xlsx"/>
                <w-btn
                    color="teal"
                    outlined
                    icon="mdi-file-export"
                    text="Générer Fichier MCS"
                    :loading="generateMcsFileContentLoading"
                    @press="generateMcsFileContent"
                />
            </template>
            <template v-slot:rightToolbar>
                <calculator-irg></calculator-irg>
                <w-btn
                    color="teal"
                    icon="mdi-import"
                    :text="$t('common.import')"
                    @press="dialog = true"
                />
                <w-btn
                    v-if="securityStore.authorities['UPDATE_IMPORT_PARAM']"
                    color="red"
                    icon="mdi-table-check"
                    :text="$t('common.apply')"
                    :loading="applyLoading"
                    @press="apply"
                />
            </template>

            <template v-slot:content>
                <v-data-table
                    v-model="selected"
                    :headers="headers"
                    :items="paySlipStore.paySlips"
                    :page.sync="page"
                    :items-per-page="itemsPerPage"
                    @page-count="pageCount = $event"
                    hide-default-footer
                    must-sort
                    single-select
                    fixed-header
                    height="250"
                    :multi-sort="false"
                    :loading="loading"
                    dense
                    @click:row="select"
                    :item-class="rowColor"
                >

                    <template v-slot:[`item.annexMonth`]="{ item }">
                        <w-chip color="deep-orange" :text="item.annexMonth"/>
                    </template>
                    <template v-slot:[`item.workerCode`]="{ item }">
                        <w-chip color="teal" :text="item.workerCode"/>
                    </template>
                    <template v-slot:[`item.workerLastName`]="{ item }">
                        <w-chip color="blue darken-2" :text="item.workerLastName"/>
                    </template>
                    <template v-slot:[`item.workerFirstName`]="{ item }">
                        <w-chip color="blue darken-2" :text="item.workerFirstName"/>
                    </template>
                    <template v-slot:[`item.workerOccupationName`]="{ item }">
                        <w-chip color="purple" :text="item.workerOccupationName"/>
                    </template>
                    <template v-slot:[`item.workerOrganizationalChartName`]="{ item }">
                        <w-chip color="cyan" :text="item.workerOrganizationalChartName"/>
                    </template>
                    <template v-slot:[`item.grille`]="{ item }">
                        <w-chip color="orange" :text="item.grille"/>
                    </template>
                    <template v-slot:[`item.baseCotisable`]="{ item }">
                        {{ priceFormat(item.baseCotisable) }}
                    </template>
                    <template v-slot:[`item.baseImposable`]="{ item }">
                        {{ priceFormat(item.baseImposable) }}
                    </template>
                    <template v-slot:[`item.totalGains`]="{ item }">
                        <w-chip color="teal" :text="priceFormat(item.totalGains)"/>
                    </template>
                    <template v-slot:[`item.totalRetenues`]="{ item }">
                        <w-chip color="red" :text="priceFormat(item.totalRetenues)"/>
                    </template>
                    <template v-slot:[`item.net`]="{ item }">
                        <w-chip color="blue" :text="priceFormat(item.net)"/>
                    </template>
                    <template v-slot:[`item.totalCnas`]="{ item }">
                        {{ priceFormat(item.totalCnas) }}
                    </template>
                    <template v-slot:[`item.masse`]="{ item }">
                        {{ priceFormat(item.masse) }}
                    </template>
                    <template v-slot:footer="{}">
                        <br/>
                        <div class="text-center">
                            <v-row justify="center">
                                <v-col md="6">
                                    <v-pagination
                                        v-model="page"
                                        :length="pageCount"
                                    ></v-pagination>
                                </v-col>
                                <v-col md="2">
                                    <w-enum-select
                                        v-model="itemsPerPage"
                                        :items="paginationListLength"
                                        :label="$t('common.items_per_page')"
                                    />
                                </v-col>
                            </v-row>
                        </div>
                    </template>
                </v-data-table>
                <BlankPaySlipDetails :pay-slip="selectedOne"/>
            </template>
        </w-app>
        <v-dialog
            v-model="dialog"
            persistent
            max-width="700"
        >
            <v-card>
                <v-toolbar flat height="40" dark color="teal">
                    <v-toolbar-title>{{ $t('common.import') }}</v-toolbar-title>
                </v-toolbar>
                <v-card-text>
                    <v-container fluid>
                        <v-row dense>
                            <v-col cols="12">
                                <strong>{{ $t('common.file') }} *</strong>
                                <w-file-input
                                    v-model="file"
                                    :rules="[mxRules.required]"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn
                        depressed
                        outlined
                        color="teal"
                        @click="dialog = false"
                    >
                        {{ $t('common.close') }}
                        <v-icon right>mdi-close</v-icon>
                    </v-btn>
                    <v-btn
                        depressed
                        class="white--text"
                        color="teal"
                        :loading="loading"
                        @click="upload"
                    >
                        {{ $t('common.import') }}
                        <v-icon right>mdi-import</v-icon>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <router-view/>
    </div>
</template>

<script>
import {ref, watch} from "vue";
import CalculatorIrg from "@/views/PaySlip/Components/CalculatorIrg.vue";
import BlankPaySlipDetails from "@/views/BlankPaySlip/BlankPaySlipDetails.vue";
import useSharedMethod from "@/use/SharedMethod";
import useSharedData from "@/use/SharedData";
import useTools from "@/use/Tools";
import {usePaySlipStore} from "@/stores/PaySlip";
import axios from "axios";
import useValidation from "@/use/Validation";
import ImportModel from "@/views/Tools/Components/ImportModel.vue";
import {useSecurityStore} from "@/stores/Security";

export default {
    name: "PaySlipTools",
    components: {
        ImportModel,
        BlankPaySlipDetails,
        CalculatorIrg
    },
    setup() {
        const {mxRules} = useValidation();
        const {$i18n, $swal} = useTools();
        const {priceFormat} = useSharedMethod();
        const {
            loading,
            page,
            itemsPerPage,
            pageCount,
            paginationListLength,
            total,
            itemColor,
        } = useSharedData();

        const paySlipStore = usePaySlipStore();
        const securityStore = useSecurityStore();

        const rightDrawer = ref(false);

        const selected = ref([])
        const headers = ref([]);
        itemsPerPage.value = 5;


        const selectedOne = ref({details: []});
        const file = ref(null);
        const dialog = ref(false);

        function upload() {
            loading.value = true;
            selectedOne.value = {details: []};
            let formData = new FormData();
            formData.append('file', file.value);
            axios.post(`/tools/getPaySlips`, formData).then(response => {
                paySlipStore.setPaySlips(response.data);
                total.value = response.data.length;

                headers.value = [];
                addHeaderHead();
                addFooterHeader();
                paySlipStore.paySlips.forEach((element) => {
                    element.details.forEach((detail) => {
                        if (detail.printIn !== "5" && detail.value !== 0) {
                            element[detail.code] = priceFormat(detail.value);
                            addHeader(detail);
                        }
                    });
                });

                select(paySlipStore.paySlips[0]);

                dialog.value = false;
            }).finally(() => {
                loading.value = false;
            })
        }

        const applyLoading = ref(false);

        function apply() {
            applyLoading.value = true;
            let formData = new FormData();
            formData.append('file', file.value);
            axios.post(`/tools/applyPaySlips`, formData).then(() => {
                $swal.fire('Très Bien', "Importation des paies terminer", 'success')
                paySlipStore.setPaySlips([]);
                selectedOne.value = {details: []};
            }).finally(() => {
                applyLoading.value = false;
            })
        }

        const generateMcsFileContentLoading = ref(false);

        function generateMcsFileContent() {
            generateMcsFileContentLoading.value = true;
            axios.get("/tools/generateMcsFileContent", {
                responseType: "arraybuffer",
            }).then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", "WIMS.MCS");
                document.body.appendChild(link);
                link.click();
            }).finally(() => {
                generateMcsFileContentLoading.value = false;
            });
        }

        function addHeaderHead() {
            headers.value.push(
                {
                    text: $i18n.t("common.month"),
                    align: "start",
                    sortable: true,
                    value: "annexMonth",
                    ordre: -8,
                },
                {
                    text: $i18n.t("common.code"),
                    align: "start",
                    sortable: true,
                    value: "workerCode",
                    ordre: -7,
                },
                {
                    text: $i18n.t("common.last_name"),
                    align: "start",
                    sortable: true,
                    value: "workerLastName",
                    ordre: -6,
                },
                {
                    text: $i18n.t("common.first_name"),
                    align: "start",
                    sortable: true,
                    value: "workerFirstName",
                    ordre: -5,
                },
                {
                    text: $i18n.t("module.occupation"),
                    align: "start",
                    sortable: true,
                    value: "workerOccupationName",
                    ordre: -4,
                },
                {
                    text: $i18n.t("module.organizational_chart"),
                    align: "start",
                    sortable: true,
                    value: "workerOrganizationalChartName",
                    ordre: -3,
                },
                {
                    text: $i18n.t("common.grille"),
                    align: "start",
                    sortable: true,
                    value: "grille",
                    order: -2,
                }
            );
        }

        function addHeader(detail) {
            if (!headers.value.some((header) => header.value === detail.code)) {
                headers.value.push({
                    text: detail.label,
                    align: "center",
                    sortable: false,
                    value: detail.code,
                    ordre: detail.ordre + 7,
                });
            }
        }

        function addFooterHeader() {
            headers.value.push(
                {
                    text: $i18n.t("common.base_imposable"),
                    align: "start",
                    sortable: true,
                    value: "baseImposable",
                    ordre: 3000,
                },
                {
                    text: $i18n.t("common.base_cotisable"),
                    align: "start",
                    sortable: true,
                    value: "baseCotisable",
                    ordre: 3001,
                },
                {
                    text: $i18n.t("pay_section.total_gains"),
                    align: "start",
                    sortable: true,
                    value: "totalGains",
                    ordre: 3002,
                },
                {
                    text: $i18n.t("pay_section.total_retenues"),
                    align: "start",
                    sortable: true,
                    value: "totalRetenues",
                    ordre: 3002,
                },
                {
                    text: $i18n.t("common.net"),
                    align: "start",
                    sortable: true,
                    value: "net",
                    ordre: 3003,
                },
                {
                    text: "Total CNAS",
                    align: "start",
                    sortable: true,
                    value: "totalCnas",
                    ordre: 3011,
                },
                {
                    text: $i18n.t("pay_slip.masse"),
                    align: "start",
                    sortable: true,
                    value: "masse",
                    ordre: 3012,
                }
            );
        }

        function select(item) {
            selectedOne.value = item;
        }

        function rowColor(item) {
            if (selectedOne.value.workerCode === item.workerCode)
                return itemColor.value;
        }


        watch(() => page.value, () => {
            select(paySlipStore.paySlips[(page.value - 1) * itemsPerPage.value]);
        });

        return {
            securityStore,
            mxRules,
            paySlipStore,
            loading,
            applyLoading,
            dialog,
            rightDrawer,
            page,
            itemsPerPage,
            pageCount,
            paginationListLength,
            total,
            headers,
            selected,
            selectedOne,
            file,
            generateMcsFileContentLoading,
            rowColor,
            select,
            upload,
            apply,
            generateMcsFileContent
        }
    },
}
</script>

<style scoped>

</style>